import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertUtils } from '../core/utils/alert-utils';
import Swal from 'sweetalert2';
import { DateHelpers } from '../core/utils/date-helpers';
import { ReservaDTO } from '../core/types';
import { take } from 'rxjs';
import { ApiService } from '../core/services/api.service';

@Component({
  selector: 'app-reserve-update-form',
  templateUrl: './reserve-update-form.component.html',
  styleUrls: ['./reserve-update-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReserveUpdateFormComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  public reserva = {} as ReservaDTO;
  id_externo: string | null = null;

  loadingProrrogar = false;
  loadingEncerrar = false;

  notificacaoValida: boolean | null = null;
  notificacaoMsg = 'Esta solicitação já foi respondida ou não está mais disponível.';

  constructor(
    private route: ActivatedRoute,
    private api: ApiService,
    private router: Router
  ) {}

  ngOnInit(): void {
    document.body.classList.add('ru-open');

    this.route.params.pipe(take(1)).subscribe(params => {
      const uuid = params['uuid'];
      this.id_externo = params['id_externo'] || null;

      if (!uuid || !this.id_externo) {
        this.router.navigate(['/404']);
        return;
      }

      // 1) dispara ambas as chamadas (reserva + verificação)
      this.loading = true;

      // buscar reserva
      this.api.getReserva(uuid).pipe(take(1)).subscribe({
        next: (res) => {
          this.reserva = res;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.router.navigate(['/404']);
        }
      });

      // verificar notificação
      this.api.verificarNotificacao(this.id_externo).pipe(take(1)).subscribe({
        next: () => {
          this.notificacaoValida = true;    // aguardando resposta → mostra botões
        },
        error: (err) => {
          // 404/400/etc → já respondida / expirada / inválida
          this.notificacaoValida = false;
          // opcionalmente personalize a mensagem vinda do backend:
          if (err?.error?.message) this.notificacaoMsg = err.error.message;
        }
      });
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('ru-open');
    this.id_externo = null;
  }

  // --- ENCERRAR ---
  async onEncerrar() {
    if (!this.notificacaoValida) return;  // proteção extra

    const confirmar = await AlertUtils.showConfirmForm(
      'Encerrar a reserva',
      'Tem certeza que deseja encerrar a reserva?'
    );
    if (!confirmar || !this.id_externo) return;

    this.loadingEncerrar = true;
    const payload = { idExterno: this.id_externo, dataSaida: null };

    this.api.salvarRespostaAtualizacaoReserva(payload).pipe(take(1)).subscribe({
      next: async () => {
        this.loadingEncerrar = false;
        await Swal.fire({ icon: 'success', title: 'Reserva encerrada.' });
        this.loadPageAgain();
      },
      error: async (err) => {
        this.loadingEncerrar = false;
        await Swal.fire({ icon: 'error', title: 'Falha ao encerrar', text: err?.error?.message || 'Tente novamente.' });
      }
    });
  }

  // --- PRORROGAR ---
  async onProrrogar() {
    if (!this.notificacaoValida) return;
    if (!this.reserva || !this.id_externo) return;

    const minDate = DateHelpers.addDays(this.reserva.dataSaidaPrevista!, 1);
    const minYMD = DateHelpers.toYMD(minDate);

    const { value: novaData } = await Swal.fire<string>({
      title: 'Prorrogar a reserva',
      text: 'Selecione a nova data de saída',
      icon: 'question',
      input: 'date',
      inputValue: minYMD,
      inputAttributes: { min: minYMD },
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      preConfirm: (val) => {
        if (!val) {
          Swal.showValidationMessage('Selecione uma data válida.');
          return false;
        }
        if (val < minYMD) {
          Swal.showValidationMessage(`A data deve ser a partir de ${DateHelpers.brDate(minDate)}.`);
          return false;
        }
        return val;
      }
    });

    if (!novaData) return;

    this.loadingProrrogar = true;
    const payload = { idExterno: this.id_externo, dataSaida: novaData };

    this.api.salvarRespostaAtualizacaoReserva(payload).pipe(take(1)).subscribe({
      next: async () => {
        this.loadingProrrogar = false;
        await Swal.fire({
          icon: 'success',
          title: 'Reserva prorrogada!',
          text: `Nova saída: ${DateHelpers.brDateYMD(novaData)}`
        });
        this.loadPageAgain();
      },
      error: async (err) => {
        this.loadingProrrogar = false;

        // Backend pode retornar 400 se a data for inválida (ex.: < data fim atual)
        await Swal.fire({
          icon: 'error',
          title: 'Não foi possível prorrogar',
          text: err?.error?.message || 'Tente uma data diferente.'
        });
      }
    });
  }

  // util opcional usado no template
  getMoradorPrincipal(reserva: ReservaDTO): string {
    const m = reserva.moradores?.find(x => x.moradorPrincipal);
    return m ? m.nome : '—';
  }
  
  private loadPageAgain(): void {
    this.loading = true;
    this.notificacaoValida = null;
    this.notificacaoMsg = 'Esta solicitação já foi respondida ou não está mais disponível.';

    // reutiliza o mesmo trecho do ngOnInit
    const uuid = this.reserva?.uuid;
    if (!uuid || !this.id_externo) return;

    this.api.getReserva(uuid).pipe(take(1)).subscribe({
      next: (res) => {
        this.reserva = res;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.router.navigate(['/404']);
      }
    });

    this.api.verificarNotificacao(this.id_externo).pipe(take(1)).subscribe({
      next: () => {
        this.notificacaoValida = true;
      },
      error: (err) => {
        this.notificacaoValida = false;
        if (err?.error?.message) this.notificacaoMsg = err.error.message;
      }
    });
  }
}
